import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  ModalBody,
  ModalContent,
  ModalFooter,
  VStack,
} from "@chakra-ui/react";
import { useDynamicModalContext } from "@umami/components";
import { getAccountGroupLabel } from "@umami/core";
import { accountsActions, useImplicitAccounts } from "@umami/state";
import { groupBy } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { AccountSelectorPopover } from "./AccountSelectorPopover";
import { useColor } from "../../styles/useColor";
import { AccountTile } from "../AccountTile";
import { ModalCloseButton } from "../CloseButton";
import { OnboardOptionsModal } from "../Onboarding/OnboardOptions";

export const AccountSelectorModal = () => {
  const implicitAccounts = useImplicitAccounts();
  const color = useColor();
  const { openWith, onClose } = useDynamicModalContext();
  const lastItemRef = useRef<HTMLDivElement>(null);
  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowShadow(!entry.isIntersecting);
      },
      {
        threshold: 1,
      }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const dispatch = useDispatch();

  const groupedAccounts = groupBy(implicitAccounts, getAccountGroupLabel);

  // TODO: Add derive account button https://linear.app/tezos/issue/UMA-1771/multi-chain-add-derive-seedphrase-account-from-the-same-seedphrase
  // const showDeriveAccountButton = (account?: ImplicitAccount) =>
  //   account && account.type === "mnemonic";

  const handleAddAccount = () => openWith(<OnboardOptionsModal />);

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalBody flexDirection="column">
        <VStack
          overflowY="auto"
          width="100%"
          maxHeight="430px"
          data-testid="account-selector-modal"
          divider={<Divider _last={{ display: "none" }} />}
          spacing="18px"
        >
          {Object.entries(groupedAccounts).map(([type, accounts]) => (
            <Flex key={type} flexDirection="column" width="100%">
              <Center
                justifyContent="space-between"
                marginBottom="18px"
                paddingRight="17px"
                paddingLeft="12px"
              >
                <Heading color={color("900")} size="md">
                  {type}
                </Heading>
                {/* TODO: Add derive account button https://linear.app/tezos/issue/UMA-1771/multi-chain-add-derive-seedphrase-account-from-the-same-seedphrase*/}
                {/* <Flex gap="12px">
                  {showDeriveAccountButton(accounts[0]) && (
                    <IconButton
                      color={color("500")}
                      aria-label={`Add ${type} account`}
                      icon={<PlusCircleIcon />}
                      onClick={() => {
                        trackAccountEvent("derive_account_from_mnemonic");

                        return openWith(
                          <DeriveMnemonicAccountModal account={accounts[0] as MnemonicAccount} />
                        );
                      }}
                      size="sm"
                      variant="ghost"
                    />
                  )}
                </Flex> */}
              </Center>
              {accounts.map(account => {
                const address = account.address.pkh;
                const onClick = () => {
                  dispatch(accountsActions.setCurrent(address));
                  onClose();
                };

                return (
                  <AccountTile
                    key={address}
                    alignItems="center"
                    account={account}
                    onClick={onClick}
                  >
                    <AccountSelectorPopover account={account} />
                  </AccountTile>
                );
              })}
            </Flex>
          ))}

          {/* This is a hack to toggle the shadow on the footer button*/}
          <Box ref={lastItemRef} paddingBottom="40px" />
        </VStack>
      </ModalBody>
      <ModalFooter
        position="relative"
        height="23px"
        margin="0"
        paddingX={{ base: "32px", md: "42px" }}
      >
        <Flex
          position="absolute"
          top="-25px"
          justifyContent="center"
          width="full"
          padding="8px"
          background="white"
          borderRadius="100px"
          boxShadow={
            showShadow
              ? color(
                  "0px -4px 10px 0px rgba(45, 55, 72, 0.10)",
                  "0px -4px 10px 0px rgba(0, 0, 0, 0.20)"
                )
              : "transparent"
          }
          transition="box-shadow 0.2s ease-in"
          backdropFilter="blur(40px)"
        >
          <Button width="full" onClick={handleAddAccount} variant="primary">
            Add account
          </Button>
        </Flex>
      </ModalFooter>
    </ModalContent>
  );
};
